import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useUpdateEngineActions } from "tap/store/updateEngine/updateEngine.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates UpdateEngine data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchJobs function and state values
 */
export function useUpdateEngineData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateUpdateEngine } = useUpdateEngineActions();
  const { filterForm, updateEngineRange, updateEngineSort, itemsPerPage } =
    useSelector((s) => s.updateEngine);

  /**
   * Fetches jobs from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,19")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array (e.g., ["field", "ASC"]) to use instead of Redux sort
   * @param {boolean} silent - If true, don't show loading indicator (for auto-refresh)
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchJobs = useCallback(
    async (range, customFilterData = null, customSort = null, silent = false) => {
      const startTime = Date.now();
      if (!silent) {
        setLoadingTrue();
      }

      const filterData = customFilterData ?? filterForm;
      const sortData = customSort ?? updateEngineSort;

      try {
        const data = await tapApi.updateEngine(
          filterCriteria(filterData, "", "updateEngine"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          if (!silent) {
            showErrorToast(data?.message || "Failed to fetch jobs data.");
          }
          return null;
        }

        return data;
      } catch (error) {
        if (!silent) {
          showErrorToast(error?.message || "Error fetching jobs data.");
        }
        console.error("Error fetching jobs data:", error);
        return null;
      } finally {
        if (!silent) {
          // Ensure minimum loading duration for consistent UX
          const elapsed = Date.now() - startTime;
          const remainingTime = MIN_LOADING_DURATION - elapsed;

          if (remainingTime > 0) {
            setTimeout(() => setLoadingFalse(), remainingTime);
          } else {
            setLoadingFalse();
          }
        }
      }
    },
    [
      filterForm,
      updateEngineSort,
      setLoadingTrue,
      setLoadingFalse,
      showErrorToast,
    ],
  );

  return {
    fetchJobs,
    updateUpdateEngine,
    itemsPerPage,
    updateEngineRange,
    filterForm,
    updateEngineSort,
  };
}
